import { isTrue, type TeamData, type TeamUpdateData } from '@terros/common'
import { Switch } from '@mantine/core'
import { useToastError } from '@hooks'
import { useTranslation, useUpdateTeam } from '@front/model'
import { type OptionalRowInPlaceEdit } from '../../OptionalRow'

export type ToggleTeamFieldOptions = {
  label: string
  disabled?: boolean
  getValue: (team: TeamData) => boolean | undefined
  toUpdates: (value: boolean) => Partial<TeamUpdateData>
}

export const useToggleTeamField = (team: TeamData, options: ToggleTeamFieldOptions): OptionalRowInPlaceEdit => {
  const { t } = useTranslation()
  const { update, isUpdating, error } = useUpdateTeam()
  useToastError(error)

  const disabled = team.isDeleted || options.disabled || isUpdating

  const toggle = async (next: boolean): Promise<void> => {
    if (disabled) return
    try {
      await update({ teamId: team.teamId, ...options.toUpdates(next) })
    } catch {
      // useToastError reports it and the switch never moved, so there is nothing to undo
    }
  }

  return {
    type: 'in-place',
    disabled,
    editor: (
      <Switch
        aria-label={t(options.label)}
        checked={isTrue(options.getValue(team))}
        disabled={disabled}
        onChange={(event) => toggle(event.currentTarget.checked)}
      />
    ),
  }
}
